import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import constants from './constants';
import weekday from 'dayjs/plugin/weekday';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

interface SelectedSite {
  siteId: string | number;
  timeZoneName?: string;
  openingTime: string;
  closingTime: string;
}

// Parse time string and convert to total minutes
export function getMinutes(time: string | Date): number {
  const timeObject = dayjs(time, ['hh:mm A', 'HH:mm']);
  const hour = timeObject.hour();
  const minute = timeObject.minute();
  return hour * 60 + minute;
}

export function getCurrentHour(timeZone: string): number {
  return dayjs().tz(timeZone).hour();
}

export function getWeekday(): number {
  return dayjs().weekday();
}

// Check if current time is within a given range
export function isCurrentTimeWithinRange(
  startTime: number,
  endTime: number
): boolean {
  const currentMinutes = getMinutes(new Date()); // now
  return currentMinutes >= startTime && currentMinutes < endTime;
}

export function convertTimeZone(
  value: string | dayjs.Dayjs,
  timeZone?: string
): string {
  const timezone = timeZone ?? constants.defaultTimeZone;

  return dayjs.utc(value).tz(timezone).format('hh:mm A');
}

export function isSiteOpen(
  promiseDateTime: Date | string | null,
  selectedSite: SelectedSite
): boolean {
  const timezone = selectedSite.timeZoneName ?? constants.defaultTimeZone;

  const australiaDate = promiseDateTime
    ? dayjs(promiseDateTime).tz(timezone)
    : dayjs().tz(timezone);

  const promiseTime = australiaDate.hour() * 60 + australiaDate.minute();
  const openingTime = getMinutes(selectedSite.openingTime);
  let closingTime = getMinutes(selectedSite.closingTime);

  if (openingTime === closingTime) {
    return false;
  }

  if (closingTime === 0) {
    closingTime = 1440;
  }

  if (openingTime < closingTime) {
    return promiseTime >= openingTime && promiseTime < closingTime;
  } else if (openingTime > closingTime) {
    return promiseTime < closingTime || promiseTime > openingTime;
  }

  return false;
}

function isBreakfastClosed(
  timeZoneName: any,
  orderTime: string | Date
): boolean {
  const timezone = timeZoneName ? timeZoneName : constants.defaultTimeZone;
  const orderDateTime: any = dayjs(orderTime).tz(timezone);
  const newDateTime: any = dayjs().tz(timezone);
  const australiaTime = orderTime
    ? getMinutes(orderDateTime)
    : getMinutes(newDateTime);
  const breakfastTime = getMinutes(constants.breakfastTime);
  return australiaTime >= breakfastTime;
}

function displayDatetime(value: Date): any {
  return dayjs(value).format('DD-MM-YYYY hh:mm A');
}

function displayDate(date: Date): any {
  return dayjs(date).format('YYYY-MM-DD');
}

function displayTime(date: Date): any {
  return dayjs(date).format('hh:mm A');
}

function displayFromNow(value: string | number | Date | any): string {
  // Pass the value to dayjs and call the fromNow() method
  return dayjs(value).fromNow();
}

export default {
  getMinutes,
  getWeekday,
  getCurrentHour,
  isCurrentTimeWithinRange,
  isSiteOpen,
  convertTimeZone,
  isBreakfastClosed,
  displayDate,
  displayDatetime,
  displayTime,
  displayFromNow,
};
